/*
 * "Find tilbehør til din enhed" - søgefeltet på forsiden.
 *
 * Shopifys predictive search rangerer kollektioner efter hvornår de er
 * oprettet, ikke efter hvor godt de passer. Enhedens hovedkategori er den
 * ældste i sin familie, så "iPhone 17 Cover & Tilbehør" lander nederst blandt
 * sine egne underkategorier - og ryger helt ud af listen, så snart der er mere
 * end ti kategorier der matcher. Det er præcis den kategori kunden leder efter
 * her, så vi henter den selv.
 *
 * Alle enhedskategorier hedder "<enhed> Cover & Tilbehør" (641 ud af 642 i
 * kataloget), og det er nok til at spørge målrettet: den ene søgning er
 * kundens ord plus "tilbehør", den anden er kundens ord alene. Resultaterne
 * bliver lagt sammen, enhederne øverst.
 *
 * Kun søgefeltet på forsiden får den behandling, og det sker ved at bytte
 * getResults ud på netop den ene instans. Et selvstændigt element var pænere at
 * læse, men temaet viser først resultaterne med reglen
 * "predictive-search[open] .predictive-search" - bytter man tagget ud, bliver
 * listen hentet og sat ind, og så står den usynlig.
 */
(() => {
  const SELECTOR = '.find-device__search predictive-search';
  const DEVICE_SUFFIX = 'cover & tilbehør';
  const BOOST_TERM = 'tilbehør';
  const MAX_ITEMS = 10;

  const normalise = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();

  // Links inde i et <template> har ingen browsing context, så link.pathname er
  // tom for dem alle - og så ligner alle resultater hinanden, når de skal
  // sammenlignes. Derfor læses href som den står, uden Shopifys sporingsdata.
  const linkKey = (link) => (link.getAttribute('href') || '').split('?')[0];

  /**
   * Fetches the theme's rendered results for one search term.
   * @param {string} term - Search query.
   * @returns {Promise<string>}
   */
  const fetchResults = async (term) => {
    const params = [
      `q=${encodeURIComponent(term)}`,
      `${encodeURIComponent('resources[limit]')}=10`,
      `${encodeURIComponent('resources[limit_scope]')}=each`,
      `${encodeURIComponent('resources[type]')}=collection`,
      'section_id=predictive-search',
    ].join('&');

    const response = await fetch(`${theme.routes.predictiveSearch}?${params}`);
    if (!response.ok) throw new Error(response.status);
    return response.text();
  };

  /**
   * Turns a response into a document fragment we can move nodes around in.
   * @param {string} html - Response body.
   * @returns {DocumentFragment}
   */
  const parse = (html) => {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    return tmpl.content;
  };

  /**
   * The list of collection results inside a parsed response.
   * @param {DocumentFragment} content - Parsed response.
   * @returns {HTMLElement|null}
   */
  const listIn = (content) => content.querySelector('#psearch-collections .predictive-search__list')
    || content.querySelector('.predictive-search__list');

  /**
   * Moves the device categories to the front of the list.
   * @param {HTMLElement} list - List to reorder (from the plain search).
   * @param {HTMLElement|null} deviceList - List from the "+ tilbehør" search.
   */
  const promoteDevices = (list, deviceList) => {
    const seen = new Map();
    list.querySelectorAll('.predictive-search__item:not(.js-submit)').forEach((item) => {
      const link = item.querySelector('a[href]');
      if (link) seen.set(linkKey(link), item);
    });

    const candidates = deviceList
      ? Array.from(deviceList.querySelectorAll('.predictive-search__item:not(.js-submit)'))
      : [];

    const devices = candidates
      .filter((item) => {
        const title = item.querySelector('.predictive-result__title');
        return title && normalise(title.textContent).endsWith(DEVICE_SUFFIX);
      })
      // Alfabetisk, med tal læst som tal: så står enheden selv først og
      // varianterne (Pro, Pro Max, Plus, FE) lige under den, i den rækkefølge
      // kunden også ser dem i menuen.
      .sort((a, b) => a.textContent.trim().localeCompare(b.textContent.trim(), 'da', { numeric: true }));

    // Bagfra, så den første i rækken ender øverst efter alle prepends.
    devices.reverse().forEach((device) => {
      const link = device.querySelector('a[href]');
      if (!link) return;

      const key = linkKey(link);
      const duplicate = seen.get(key);
      if (duplicate) duplicate.remove();

      list.prepend(device);
      seen.set(key, device);
    });

    // Listen er en dropdown, ikke en resultatside - klip den, men lad temaets
    // "se alle"-knap blive stående nederst.
    Array.from(list.querySelectorAll('.predictive-search__item:not(.js-submit)'))
      .slice(MAX_ITEMS)
      .forEach((item) => item.remove());
  };

  /**
   * Replaces getResults on one search field: two requests instead of one, and
   * the device categories on top of the merged list.
   * @param {HTMLElement} search - A <predictive-search> element.
   * @param {Function} themeGetResults - The theme's own implementation.
   */
  const enhance = (search, themeGetResults) => {
    if (search.dataset.deviceRanking) return;
    search.dataset.deviceRanking = 'on';

    search.getResults = async function getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      const queryKey = searchTerm.toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const [everything, devices] = await Promise.all([
          fetchResults(searchTerm),
          fetchResults(`${searchTerm} ${BOOST_TERM}`),
        ]);

        let content = parse(everything);
        let list = listIn(content);

        // Kunden har skrevet noget der kun rammer enheder - så er den anden
        // søgning hele resultatet. Rammer ingen af dem noget, bliver den første
        // stående, så "ingen resultater" nævner kundens egne ord og ikke det
        // "tilbehør" vi selv har lagt til.
        if (!list) {
          const deviceContent = parse(devices);
          const deviceList = listIn(deviceContent);

          if (deviceList) {
            content = deviceContent;
            list = deviceList;
          }
        } else {
          promoteDevices(list, listIn(parse(devices)));
        }

        const resultsEl = content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('No results section in response');

        const markup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = markup;
        this.renderResults(markup);
      } catch (error) {
        // Rangeringen er en forbedring, ikke en forudsætning: hvis noget her
        // fejler, skal søgefeltet stadig vise temaets egne resultater.
        console.error('[device-search] falling back to the theme search:', error);
        await themeGetResults.call(this, searchTerm);
      }
    };
  };

  const init = () => {
    const Base = customElements.get('predictive-search');
    if (!Base) return;

    const themeGetResults = Base.prototype.getResults;
    document.querySelectorAll(SELECTOR).forEach((search) => enhance(search, themeGetResults));
  };

  customElements.whenDefined('predictive-search').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  });
})();
